import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import FieldWrapper from './FieldWrapper';
import InFieldConfirmationValue from '../InFieldConfirmationValue';

import { isRequiredFulfilled } from '../util/input_checks';
import { 
    useHandleInputChange,
    useAreRequiredErrorsActive
} from '../context-provider/InputFormContextProvider';
import { useIsConfirmationViewOpenContext } from '../context-provider/FormBaseContextProvider';
import { computeDropdownHeight } from '../util/input_form_data_handler';
import FieldErrorMsgs from '../util/input_form_fields_error_msgs.json';

const REQUIRED_EMSG = FieldErrorMsgs.required;
const TOP_INFO_TEXT = "Die gewählte Option darf nicht 'Keine Option gewählt' sein.";
const INIT_OPTION = 'Keine Option gewählt';
const MAX_DRODPDOWN_HEIGHT = 189; /* px */

const SingleChoiceField = ({
    str_id,
    str_fieldTitle='',
    str_currentOption=INIT_OPTION, /* Standard message, used for comparison of slected value. */
    arr_options,
    str_bottomInfoText='',
    /* Multiples of 31.5 required. Digit input only (e.g. 30 NOT 30px). Default are 6 entries. */
    nbr_heightInPixels=MAX_DRODPDOWN_HEIGHT, /* Max. num of dropdown items shown. */
    b_displayTopInfoText=true,
    b_isRequired=false,
    b_isFieldDisplayed=true
}) => {

    const [isOpen, setIsOpen] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const [selectedOption, setSelectedOption] = useState(str_currentOption)
    const optionsRef = useRef(b_isRequired ? arr_options : [INIT_OPTION, ...arr_options])
    const [errorMsg, setErrorMsg] = useState('')
    const displayBarRef = useRef()
    const dropdownHeightInPixelsRef = useRef(computeDropdownHeight(optionsRef.current.length, nbr_heightInPixels))
    /* Flag to avoid error with dropdown error display. If the user clicks
     * somewhere on the page, the event listener of this component fires 
     * to keep track of closing the dropdown menu. If the meny has never been
     * open, the event listener must not show any errors (e.g. required error). */
    const wasMenuOpenRef = useRef(false)

    /* Varibles to keep track of dropdown elements that the 
     * use scrolled through via the 'tab' button. */
    let int_dropdownElsMaxLength = optionsRef.current.length
    let arr_dropdownEls = []
    
    const handleInputChange = useHandleInputChange()
    const isRequiredErrorActivate = useAreRequiredErrorsActive()
    const isConfirmationViewOpen = useIsConfirmationViewOpenContext()
    
    const hasError = (str_currentOption='') => {
        const str_value = str_currentOption ? str_currentOption : selectedOption
        if (b_isRequired && !isRequiredFulfilled(str_value, true, false)) return true
        return false
    }

    useEffect(() => {
        /**
         * Listen to click on the page to be able to close the dropdown if it is 
         * open and the user clicks elements on the page that are neither the dropdown bar
         * nor one of the dropdown elements.
         */
         const setStates = () => {
            if (wasMenuOpenRef.current) {
                if (hasError()) setErrorMsg(REQUIRED_EMSG)
                else setErrorMsg('')
                setIsClosing(true)
            }
        }

        const handleClick = (e) => {
            const target = e.target
            const isSelectedBar = target.classList.contains('select-selected')
            
            if (isSelectedBar) {
                const selectTag = target.previousElementSibling
                if (selectTag.id !== str_id) setIsOpen(false)
            } else {
                const openEl = document.querySelector('.is-open')
                if (openEl) {
                    const select = openEl.previousElementSibling
                    if (select.id === str_id) setStates()
                }
            }
        }

        window.addEventListener('click', handleClick)
        return () => { window.removeEventListener('click', handleClick) }
    }, [selectedOption])

    useMemo(() => {
        /* All the required fields that are blank and do not show erros,
         * are activated when a form is submitted. The line below is only 
         * executed once. After the first execution, the required field 
         * displays error messages until the input is correct. */
        if (isRequiredErrorActivate && b_isRequired && hasError()) setErrorMsg(REQUIRED_EMSG)
    }, [isRequiredErrorActivate])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        /* Clean up component on de-mount. */
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen, selectedOption])

    useMemo(() => {
        /* When the input form context provider renders, check if this
         * components state must be updated. If, for example, a filter 
         * input form is cleard, this hook allows changing the state. */
        setSelectedOption(str_currentOption ? str_currentOption : INIT_OPTION)
    }, [str_currentOption])

    useEffect(() => {
        /* Update the options array if new data is passed to the component. */
        optionsRef.current = b_isRequired ? arr_options : [INIT_OPTION, ...arr_options]
        const numOptions = optionsRef.current.length
        dropdownHeightInPixelsRef.current = computeDropdownHeight(numOptions, nbr_heightInPixels)
        int_dropdownElsMaxLength = numOptions
    }, [arr_options])

    useEffect(() => {
        /**
         * If the component's state is set to isClosing, this hook triggers
         * a wait function to set the state to isOpen = false after waiting
         * for the animation to be done running. The animation duration must
         * be in sync with the animation duration in animations.scss.
         */
        if (isClosing) {
            setTimeout(() => {
                setIsOpen(false)
                setIsClosing(false)
            }, 400)
        }
    }, [isClosing])

    const handleDataToParent = (str_currentOption='', b_hasError) => {
        /* If the input argument is not an empty string, then the latest options 
         * might not yet have been applied to the state, thus, the code sends 
        * the input argument up to the parent component. */
        const str_selectedOption = str_currentOption ? str_currentOption : selectedOption
        handleInputChange({
            id: str_id,
            value: str_selectedOption === INIT_OPTION ? '' : str_selectedOption,
            hasError: b_hasError
        })
    }
    
    const handleKeyDown = (e) => {
        if ((e.key === 'Escape') && isOpen) {
            const b_hasError = hasError()
            if (b_hasError) setErrorMsg(REQUIRED_EMSG)
            else setErrorMsg('')
            setIsClosing(true)
            handleDataToParent('', b_hasError)
            displayBarRef.current.focus()
        }
    }

    const barOnClick = () => {
        /* Sequence is important! First open/close, then display the error. */
        if (isOpen) {
            /* The menu is going to be closed. Check/display error when it is closed. */
            const b_hasError = hasError()
            if (b_hasError) setErrorMsg(REQUIRED_EMSG)
            else setErrorMsg('')
            handleDataToParent('', b_hasError)
        } else {
            /* Check if any other menu is open in the DOM. This is indicated by the
             * class .custom-select-selected. If one is found, click it, this triggers
             * the function handleCustomSelectSelected() in this file. This function
             * then closes the other menu and displays its error message accordingly. */
            const dom_el = document.querySelector('.custom-select-selected')
            if (dom_el) dom_el.click()
            /* Set was menu open flag. */
            wasMenuOpenRef.current = true;
        }

        if (!isOpen) setIsOpen(true)
        else setIsClosing(true)
    }

    const handleCustomSelectSelected = (e) => {
        /* This element 'e' can ONLY be clicked programmatically by the code
         * in the function bacOnClick(). Therefore, it listens to a click event.
         * If the user clicks the bar or an item, it fires an onMouseDown event to
         * avoid problems with the char counter of the text field. */
        if (e.target.classList.contains('custom-select-selected')) {
            setIsOpen(!isOpen)
            setIsClosing(false)
            if (hasError()) setErrorMsg(REQUIRED_EMSG)
            else setErrorMsg('')
        }
    }

    /* Functionality for dropdown menu items. */

    const itemOnClick = (e) => {
        /* Select the clicked item. */
        const str_chosenOption = e.target.innerHTML
        const b_hasError = hasError(str_chosenOption)
        if (b_hasError) setErrorMsg(REQUIRED_EMSG)
        else setErrorMsg('')
        setSelectedOption(str_chosenOption)
        setIsClosing(true)
        handleDataToParent(str_chosenOption, b_hasError)
        displayBarRef.current.focus()
    }

    const itemOnKeyDown = (e) => {
        if (e.key === 'Enter') {
            itemOnClick(e)
        }
        else if (e.key === 'ArrowUp') {
            /* Make sure that at least one item is stored in the array, 
             * otherwise pop() causes an error. */
            if (arr_dropdownEls.length > 0) {
                /* Prevent scrolling the window. */
                e.preventDefault()
                /* Prop the last focused element. */
                const el = arr_dropdownEls.pop()
                /* Set focus to the previous element. */
                el.focus()
                /* Pop the element that was pushed by the onBlur. */
                arr_dropdownEls.pop()
            }
        }
        else if ((e.key === 'Tab') && (arr_dropdownEls.length === int_dropdownElsMaxLength-1)) {
            if (hasError()) setErrorMsg(REQUIRED_EMSG)
            else setErrorMsg('')
            setIsOpen(false)
            /* Empty array when the user leaves it. */
            arr_dropdownEls = []
        }
    }

    const itemBlur = (e) => {
        /* If the user tabs through the dropdown menu, the elements
         * get added to an array. By moving upward in the dropdown menu, 
         * the item gets blurred as well, but no elements are added to 
         * the array. 
         */
        if (e.key !== 'ArrowUp') arr_dropdownEls.push(e.target)
    }

    return (
        isConfirmationViewOpen
        ?
        <FieldWrapper
            str_fieldTitle={str_fieldTitle}
            b_isRequired={false}
        >
            <InFieldConfirmationValue value={selectedOption} />
        </FieldWrapper>
        :
        b_isFieldDisplayed &&
        <FieldWrapper
            str_fieldTitle={str_fieldTitle}
            str_errorMsg={errorMsg}
            str_topInfoText={b_isRequired && b_displayTopInfoText ? TOP_INFO_TEXT : ''}
            str_bottomInfoText={str_bottomInfoText}
            b_isRequired={b_isRequired}
        >
            <div className=
                {`in-field custom-select ${isOpen ? 'custom-select-selected' : ''}`}
                onClick={e => handleCustomSelectSelected(e)}
            >
                {/* Elemements for the input form. */}
                <select
                    id={str_id}
                    required={b_isRequired}
                    value={selectedOption}
                    readOnly
                >
                    {optionsRef.current.map((option, index) => (
                        <option key={index} value={option}>
                            {option}
                        </option>
                    ))}
                </select>

                {/* Element that displays the currently selected item. Visible bar. */}
                <div className={`select-selected ${isOpen ? 'is-open' : ''}`}
                    tabIndex={0}
                    ref={displayBarRef}
                    onMouseDown={() => barOnClick()}
                    onKeyDown={(e) => { if (e.key === 'Enter') { barOnClick() } }}
                >
                    {selectedOption}
                </div>

                {
                    /* Elements the user engages with (artifical elements). */
                    isOpen &&
                    <div
                        className={
                            `select-items dropdown-rolldown-animation
                            ${isClosing ? 'dropdown-rollup-animation' : ''}`
                        }
                        style={
                            nbr_heightInPixels < dropdownHeightInPixelsRef.current
                            ?
                            {height: `${nbr_heightInPixels}px`, borderBottomRightRadius: '0'}
                            :
                            dropdownHeightInPixelsRef.current.toFixed(1) > MAX_DRODPDOWN_HEIGHT
                            ?
                            {height: `${MAX_DRODPDOWN_HEIGHT}px`, borderBottomRightRadius: '0'}
                            :
                            {height: `${dropdownHeightInPixelsRef.current}px`, overflow: 'hidden'}
                        }
                    >
                        {optionsRef.current.map((option, index) => (
                            <div
                                key={index} 
                                className={`${selectedOption === option ? 'is-selected' : ''}`}
                                tabIndex={0}
                                onClick={e => itemOnClick(e)}
                                onKeyDown={e => itemOnKeyDown(e)}
                                onBlur={itemBlur}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                }
            </div>
        </FieldWrapper>
    )
}

SingleChoiceField.propTypes = {
    str_id: PropTypes.string.isRequired,
    str_fieldTitle: PropTypes.string,
    nbr_heightInPixels: PropTypes.number,
    str_bottomInfoText: PropTypes.string,
    str_currentOption: PropTypes.string,
    arr_options: PropTypes.array.isRequired,
    b_displayTopInfoText: PropTypes.bool,
    b_isRequired: PropTypes.bool,
    b_isFieldDisplayed: PropTypes.bool
}

export default SingleChoiceField
